import { useState } from "react";
import { RotateCcw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation} from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { soundManager } from "@/utils/sounds";

interface ChatMessageProps {
  message: string;
  isUser: boolean;
  timestamp?: Date;
  onEdit?: () => void;
  onGoBack?: () => void;
  goBackAllowed: boolean;
  isLoading?: boolean;
}

export function ChatMessage({ message, isUser, timestamp, onEdit, onGoBack, goBackAllowed, isLoading }: ChatMessageProps) {
  const T = useTranslation().t('chatMessage', { returnObjects: true })
  const { user } = useAuth();
  const { toast } = useToast();
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState("");
  const [isSendingReport, setIsSendingReport] = useState(false);
  const connectionIssueMessage = "I'm having trouble connecting right now. Report this to our team";
  const shouldShowReportLink = !isUser && !isLoading && message === connectionIssueMessage;

  const handleSendReport = async () => {
    if (!reportMessage.trim()) {
      toast({
        title: "Error",
        description: "Please describe the issue before sending.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingReport(true);

    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          subject: "Resume Builder bot errors",
          message: reportMessage,
          userEmail: user?.email,
          userName: user?.user_metadata?.full_name
        }
      });

      if (error) throw error;

      soundManager.messageSent();
      toast({
        title: "Message sent successfully!",
        description: "We've received your report and will look into it.",
      });
      setReportMessage("");
      setReportOpen(false);
    } catch (error) {
      console.error('Error sending AI issue report:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingReport(false);
    }
  };

  const renderMessageContent = () => {
    if (shouldShowReportLink) {
      return (
        <>
          I'm having trouble connecting right now.{" "}
          <Dialog open={reportOpen} onOpenChange={setReportOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                className="font-medium text-primary underline underline-offset-2 hover:text-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                Report this to our team
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Report connection issue</DialogTitle>
                <DialogDescription>
                  Tell us what happened and our team will review it.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Textarea
                  value={reportMessage}
                  onChange={(event) => setReportMessage(event.target.value)}
                  placeholder="Describe what happened..."
                  rows={5}
                />
                <Button
                  type="button"
                  onClick={handleSendReport}
                  disabled={isSendingReport}
                  className="w-full"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isSendingReport ? "Sending..." : "Send"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      );
    }

    return message;
  };

  return (
    <div className={`flex flex-col gap-2 ${isUser ? "items-end" : "items-start"} mb-4`}>
      <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
        {!isUser && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
            <img
              src="/lovable-uploads/c96cfab8-cc2c-42cf-afbf-d5cab483bb7d.png"
              alt="AI Assistant"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div
          className={`max-w-[80%] rounded-2xl px-4 py-3 ${isUser
            ? "bg-chat-user text-white"
            : "bg-chat-ai text-foreground"
            }`}
        >
          {isLoading ? (
            <div className="flex items-center gap-1">
              <span>{message}</span>
              <div className="flex gap-1">
                <div className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          ) : (
            renderMessageContent()
          )}
          {timestamp && !isLoading && (
            <p className="text-[10px] sm:text-xs opacity-70 mt-1">
              {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {isUser && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
            <img
              src="/lovable-uploads/01ea8ad5-5921-4cfb-935d-a901c0253a53.png"
              alt="User"
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Action buttons for user messages */}
      {isUser && onGoBack && goBackAllowed && (
        <div className="flex gap-2 mr-11">
          <Button
            variant="ghost"
            size="sm"
            onClick={onGoBack}
            className="h-6 px-2 text-xs hover:bg-primary-light flex items-center gap-1"
          >
            <RotateCcw className="h-3 w-3" />
            {T["back"]}
          </Button>
        </div>
      )}
    </div>
  );
}
